import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabBarVisibility } from '../../contexts/TabBarVisibilityContext';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBrowserSessions, revokeBrowserSession } from '../../api/browserSessions';
import { getAuthChannel } from '../../utils/miniappAccessToken';
import { isTelegramWebAppEnvironment } from '../../utils/telegramEnvironment';
import Button from '../../components/Button';
import './SessionsPage.css';

function formatSessionDate(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts * 1000);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Краткое имя устройства из User-Agent */
function formatDeviceLabel(userAgent) {
  const ua = (userAgent || '').trim();
  if (!ua) return 'Неизвестное устройство';
  const l = ua.toLowerCase();
  let os = '';
  if (l.includes('iphone') || l.includes('ipad') || (l.includes('mac') && l.includes('mobile'))) {
    os = l.includes('ipad') ? 'iPad' : 'iPhone';
  } else if (l.includes('android')) os = 'Android';
  else if (l.includes('windows')) os = 'Windows';
  else if (l.includes('mac os') || l.includes('macintosh')) os = 'macOS';
  else if (l.includes('linux')) os = 'Linux';
  let browser = '';
  if (l.includes('edg/')) browser = 'Edge';
  else if (l.includes('chrome') && !l.includes('edg')) browser = 'Chrome';
  else if (l.includes('safari') && !l.includes('chrome')) browser = 'Safari';
  else if (l.includes('firefox')) browser = 'Firefox';
  else if (l.includes('telegram')) browser = 'Telegram';
  if (os && browser) return `${browser} · ${os}`;
  if (os) return os;
  if (browser) return browser;
  return ua.length > 48 ? `${ua.slice(0, 45)}…` : ua;
}

export default function SessionsPage() {
  const navigate = useNavigate();
  const { setTabBarVisible } = useTabBarVisibility();
  const { logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revokingSid, setRevokingSid] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchBrowserSessions();
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить сессии');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setTabBarVisible(false);
    const tg = window.Telegram?.WebApp;
    const backButton = tg?.BackButton;
    if (!backButton) {
      return () => setTabBarVisible(true);
    }
    const handleBack = () => navigate(-1);
    backButton.onClick(handleBack);
    backButton.show();
    return () => {
      backButton.offClick(handleBack);
      backButton.hide();
      setTabBarVisible(true);
    };
  }, [navigate, setTabBarVisible]);

  useEffect(() => {
    load();
  }, [load]);

  const onRevoke = async (sid, isCurrent) => {
    if (revokingSid) return;
    setRevokingSid(sid);
    try {
      await revokeBrowserSession(sid);
      if (isCurrent && getAuthChannel() === 'browser') {
        await logout();
        navigate('/browser-login', { replace: true });
        return;
      }
      await load();
    } catch (e) {
      setError(e.message || 'Не удалось завершить сессию');
    } finally {
      setRevokingSid(null);
    }
  };

  return (
    <div className="sessions-page page-container">
      <div className="sessions-page__header">
        <div className="sessions-page__header-text">Устройства и сессии</div>
      </div>

      <p className="sessions-page__lead">
        Активные входы через браузер. В приложении Telegram отдельные сессии не отображаются.
      </p>

      {error && <p className="sessions-page__error">{error}</p>}

      {loading ? (
        <p className="sessions-page__muted">Загрузка…</p>
      ) : sessions.length === 0 ? (
        <p className="sessions-page__muted">
          Нет активных браузерных сессий. Они появляются после входа с сайта в обычном браузере.
        </p>
      ) : (
        <ul className="sessions-page__list">
          {sessions.map((s) => (
            <li key={s.sid} className="sessions-page__card">
              <div className="sessions-page__card-main">
                <div className="sessions-page__device">{formatDeviceLabel(s.user_agent)}</div>
                {s.is_current ? (
                  <span className="sessions-page__badge-current">Текущее устройство</span>
                ) : null}
                {s.ip ? <div className="sessions-page__meta">IP: {s.ip}</div> : null}
                <div className="sessions-page__meta">
                  Активность: {formatSessionDate(s.last_seen) || '—'}
                </div>
              </div>
              {!s.is_current ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="sessions-page__revoke"
                  loading={revokingSid === s.sid}
                  disabled={Boolean(revokingSid)}
                  onClick={() => onRevoke(s.sid, false)}
                >
                  Завершить
                </Button>
              ) : isTelegramWebAppEnvironment() ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="sessions-page__revoke"
                  loading={revokingSid === s.sid}
                  disabled={Boolean(revokingSid)}
                  onClick={() => onRevoke(s.sid, true)}
                >
                  Выйти на этом устройстве
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
